import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Package, Copy, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const UNITS = ['Stück', 'Karton', 'kg', 'Liter', 'Packung', 'Palette'];

export const BestellProdukte = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]); // NEW: Locations
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    supplier_article_number: '',
    internal_article_number: '',
    ean: '',
    unit: 'Stück',
    price_net: '',
    packaging_unit: '',
    description: '',
    selectedCategories: {},
    selectedLocations: {}, // NEW: Multi-location
    is_available: true,
  });

  useEffect(() => {
    fetchData();
    fetchLocations(); // NEW
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/locations`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setLocations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      toast.error('Fehler beim Laden der Standorte');
    }
  };

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/api/order-products`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/api/order-categories`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      setProducts(productsRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      // Prevent editing backshop products
      if (product.source === 'backshop') {
        toast.error('Backshop-Produkte können nicht bearbeitet werden');
        return;
      }
      
      setEditingProduct(product);
      
      // Build selectedCategories object
      const selectedCats = {};
      (product.category_ids || []).forEach(catId => {
        selectedCats[catId] = true;
      });

      // NEW: Build selectedLocations object
      const selectedLocs = {};
      (product.location_ids || []).forEach(locId => {
        selectedLocs[locId] = true;
      });

      setFormData({
        name: product.name,
        supplier_article_number: product.supplier_article_number || '',
        internal_article_number: product.internal_article_number || '',
        ean: product.ean || '',
        unit: product.unit || 'Stück',
        price_net: product.price_net?.toString() || '',
        packaging_unit: product.packaging_unit || '',
        description: product.description || '',
        selectedCategories: selectedCats,
        selectedLocations: selectedLocs, // NEW
        is_available: product.is_available !== false,
      });
    } else {
      setEditingProduct(null);
      
      // NEW: Default all locations selected
      const allLocations = {};
      locations.forEach(loc => {
        allLocations[loc.id] = true;
      });
      
      setFormData({
        name: '',
        supplier_article_number: '',
        internal_article_number: '',
        ean: '',
        unit: 'Stück',
        price_net: '',
        packaging_unit: '',
        description: '',
        selectedCategories: {},
        selectedLocations: allLocations, // NEW
        is_available: true,
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    const selectedCategoryIds = Object.keys(formData.selectedCategories).filter(
      catId => formData.selectedCategories[catId]
    );

    if (selectedCategoryIds.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Kategorie aus');
      return;
    }

    // NEW: Build location_ids array
    const location_ids = Object.keys(formData.selectedLocations).filter(
      locId => formData.selectedLocations[locId]
    );

    if (location_ids.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Standort aus');
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        supplier_article_number: formData.supplier_article_number.trim() || null,
        internal_article_number: formData.internal_article_number.trim() || null,
        ean: formData.ean.trim() || null,
        unit: formData.unit || null,
        price_net: formData.price_net ? parseFloat(formData.price_net) : null,
        packaging_unit: formData.packaging_unit.trim() || null,
        description: formData.description.trim() || null,
        category_ids: selectedCategoryIds,
        location_ids: location_ids, // NEW
        is_available: formData.is_available,
        sort_order: 0,
        customer_id: 'dummy'
      };

      if (editingProduct) {
        await axios.put(`${API}/api/order-products/${editingProduct.id}`, payload, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Produkt erfolgreich aktualisiert');
      } else {
        await axios.post(`${API}/api/order-products`, payload, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Produkt erfolgreich erstellt');
      }

      setShowDialog(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save product:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDuplicate = async (product) => {
    // Prevent duplicating backshop products
    if (product.source === 'backshop') {
      toast.error('Backshop-Produkte können nicht dupliziert werden');
      return;
    }
    
    try {
      await axios.post(`${API}/api/order-products/${product.id}/duplicate`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Produkt erfolgreich dupliziert');
      fetchData();
    } catch (error) {
      console.error('Failed to duplicate product:', error);
      toast.error('Fehler beim Duplizieren');
    }
  };

  const handleDelete = async () => {
    if (!deletingProduct) return;

    // Prevent deleting backshop products
    if (deletingProduct.source === 'backshop') {
      toast.error('Backshop-Produkte können nicht gelöscht werden');
      setShowDeleteDialog(false);
      setDeletingProduct(null);
      return;
    }

    try {
      await axios.delete(`${API}/api/order-products/${deletingProduct.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Produkt erfolgreich gelöscht');
      setShowDeleteDialog(false);
      setDeletingProduct(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const toggleCategory = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      selectedCategories: {
        ...prev.selectedCategories,
        [categoryId]: !prev.selectedCategories[categoryId]
      }
    }));
  };

  const getCategoryNames = (categoryIds) => {
    return categoryIds
      .map(catId => {
        const cat = categories.find(c => c.id === catId);
        return cat ? `${cat.icon} ${cat.name}` : null;
      })
      .filter(Boolean);
  };

  const filteredProducts = filterCategory === 'all'
    ? products
    : products.filter(p => p.category_ids?.includes(filterCategory));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Lädt...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 pb-6">
        {/* Header */}
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/bestell-kategorien')}
            className="mb-2 -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zu Kategorien
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
            Alle Produkte
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-2">
            Verwalten Sie Produkte für Ihre Bestellkategorien
          </p>
        </div>

        {/* Actions Bar */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="w-full sm:w-64">
                <Label htmlFor="filter" className="text-sm mb-2 block">Filter nach Kategorie</Label>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger id="filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Kategorien</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => handleOpenDialog()} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Neues Produkt
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Products List */}
        {filteredProducts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Noch keine Produkte</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Erstellen Sie Ihr erstes Produkt
              </p>
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Produkt erstellen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {/* Desktop: Table */}
            <div className="hidden md:block">
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-800 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Lief. Art.-Nr.</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Intern Art.-Nr.</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Preis Netto</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Kategorien</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.map((product) => (
                          <tr key={product.id} className="border-b hover:bg-gray-50 dark:bg-gray-800">
                            <td className="px-4 py-3">
                              <div className={`w-3 h-3 rounded-full ${product.is_available ? 'bg-green-500' : 'bg-gray-300'}`} />
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-sm flex items-center gap-2">
                                <span>{product.name}</span>
                                {product.source === 'backshop' && (
                                  <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700">
                                    Backshop
                                  </Badge>
                                )}
                              </div>
                              {product.packaging_unit && (
                                <div className="text-xs text-gray-500">{product.packaging_unit}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">{product.supplier_article_number || '-'}</td>
                            <td className="px-4 py-3 text-sm">{product.internal_article_number || '-'}</td>
                            <td className="px-4 py-3 text-sm">
                              {product.price_net ? `${product.price_net.toFixed(2)} €` : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {getCategoryNames(product.category_ids || []).map((name, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {name}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1 justify-end">
                                {product.source !== 'backshop' && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDuplicate(product)}
                                      title="Duplizieren"
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleOpenDialog(product)}
                                      title="Bearbeiten"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setDeletingProduct(product);
                                        setShowDeleteDialog(true);
                                      }}
                                      className="text-red-600 hover:text-red-700"
                                      title="Löschen"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                                {product.source === 'backshop' && (
                                  <span className="text-xs text-gray-500 px-2">Backshop-Produkt</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Mobile: Cards */}
            <div className="md:hidden space-y-3">
              {filteredProducts.map((product) => (
                <Card key={product.id}>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      {/* Status & Name */}
                      <div className="flex items-start gap-3">
                        <div className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 ${product.is_available ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-base">{product.name}</h3>
                            {product.source === 'backshop' && (
                              <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700">
                                Backshop
                              </Badge>
                            )}
                          </div>
                          {product.packaging_unit && (
                            <p className="text-xs text-gray-500 mt-1">{product.packaging_unit}</p>
                          )}
                        </div>
                      </div>

                      {/* Details */}
                      <div className="space-y-1 text-sm">
                        {product.supplier_article_number && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Lief. Art.-Nr.:</span>
                            <span>{product.supplier_article_number}</span>
                          </div>
                        )}
                        {product.internal_article_number && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Intern Art.-Nr.:</span>
                            <span>{product.internal_article_number}</span>
                          </div>
                        )}
                        {product.price_net && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Preis Netto:</span>
                            <span className="font-semibold">{product.price_net.toFixed(2)} €</span>
                          </div>
                        )}
                      </div>

                      {/* Categories */}
                      <div>
                        <Label className="text-xs text-gray-500 mb-1 block">Kategorien</Label>
                        <div className="flex flex-wrap gap-1">
                          {getCategoryNames(product.category_ids || []).map((name, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {name}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-2 border-t">
                        {product.source !== 'backshop' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDuplicate(product)}
                              className="flex-1"
                            >
                              <Copy className="h-4 w-4 mr-1" />
                              Duplizieren
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenDialog(product)}
                              className="flex-1"
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Bearbeiten
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setDeletingProduct(product);
                                setShowDeleteDialog(true);
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {product.source === 'backshop' && (
                          <div className="flex-1 flex items-center justify-center py-2">
                            <span className="text-xs text-gray-500">Backshop-Produkt (nur ansehen)</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Produkt bearbeiten' : 'Neues Produkt'}
            </DialogTitle>
            <DialogDescription>
              Legen Sie ein neues Produkt für Ihre Bestellkategorien an
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="z.B. Coca Cola 0,33l"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            {/* Article Numbers Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="supplier_article_number">Lief. Art.-Nr.</Label>
                <Input
                  id="supplier_article_number"
                  placeholder="z.B. CC-001"
                  value={formData.supplier_article_number}
                  onChange={(e) => setFormData({ ...formData, supplier_article_number: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="internal_article_number">Intern Art.-Nr.</Label>
                <Input
                  id="internal_article_number"
                  placeholder="z.B. INT-001"
                  value={formData.internal_article_number}
                  onChange={(e) => setFormData({ ...formData, internal_article_number: e.target.value })}
                />
              </div>
            </div>

            {/* EAN */}
            <div className="space-y-2">
              <Label htmlFor="ean">EAN-Code</Label>
              <Input
                id="ean"
                placeholder="13-stellig, z.B. 4006040050001"
                value={formData.ean}
                onChange={(e) => setFormData({ ...formData, ean: e.target.value })}
                maxLength={13}
              />
            </div>

            {/* Unit & Price Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="unit">Einheit</Label>
                <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                  <SelectTrigger id="unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(unit => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="price_net">Preis Netto (€)</Label>
                <Input
                  id="price_net"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="z.B. 12.50"
                  value={formData.price_net}
                  onChange={(e) => setFormData({ ...formData, price_net: e.target.value })}
                />
              </div>
            </div>

            {/* Packaging Unit */}
            <div className="space-y-2">
              <Label htmlFor="packaging_unit">Verpackungseinheit</Label>
              <Input
                id="packaging_unit"
                placeholder="z.B. 50 Stück pro Karton"
                value={formData.packaging_unit}
                onChange={(e) => setFormData({ ...formData, packaging_unit: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Geben Sie an, wie viele Einheiten eine Verpackung enthält
              </p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Beschreibung</Label>
              <Input
                id="description"
                placeholder="Zusätzliche Informationen..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            {/* Categories Multi-Select */}
            <div className="space-y-3">
              <Label>Kategorien * (mindestens 1)</Label>
              <div className="space-y-2 border rounded-lg p-3">
                {categories.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Keine Kategorien verfügbar. Bitte erstellen Sie zuerst Kategorien.
                  </p>
                ) : (
                  categories.map(cat => (
                    <div key={cat.id} className="flex items-center justify-between p-2 hover:bg-gray-50 dark:bg-gray-800 rounded">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{cat.icon}</span>
                        <span className="text-sm font-medium">{cat.name}</span>
                      </div>
                      <Switch
                        checked={formData.selectedCategories[cat.id] || false}
                        onCheckedChange={() => toggleCategory(cat.id)}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* NEW: Locations Multi-Select */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Label>📍 Verfügbar für Standorte</Label>
                <span className="text-xs text-gray-500">
                  ({Object.values(formData.selectedLocations).filter(Boolean).length} ausgewählt)
                </span>
              </div>
              
              {locations.length === 0 ? (
                <p className="text-sm text-gray-500">Keine Standorte gefunden</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  {locations.map((location) => (
                    <label 
                      key={location.id}
                      className="flex items-start gap-3 p-2 rounded hover:bg-white dark:bg-gray-900 transition-colors cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedLocations[location.id] || false}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            selectedLocations: {
                              ...formData.selectedLocations,
                              [location.id]: e.target.checked
                            }
                          });
                        }}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{location.name}</div>
                        {location.address && (
                          <div className="text-xs text-gray-500 truncate">{location.address}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <span>💡</span>
                <span>Dieses Produkt ist für alle gewählten Standorte verfügbar</span>
              </p>
            </div>

            {/* Availability */}
            <div className="space-y-2">
              <Label>Verfügbarkeit</Label>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${formData.is_available ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <span className="text-sm font-medium">
                    {formData.is_available ? 'Verfügbar' : 'Nicht verfügbar'}
                  </span>
                </div>
                <Switch
                  checked={formData.is_available}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave}>
              {editingProduct ? 'Speichern' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Produkt löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie das Produkt &quot;{deletingProduct?.name}&quot; wirklich löschen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingProduct(null)}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};
